import React from 'react';
import { Table, Tag, Button, Space, Tooltip } from 'antd';
import { EyeOutlined } from '@ant-design/icons';
import { formatApiDate, compareApiDates } from '../../helpers/dateUtils';
import {NAIRA} from "../../defaults/utilities";
import {formatCurrency} from "../../helpers/currency";

const TransactionTable = ({ transactions, onView, loading }) => {
  const getTransactionTypeColor = (type) => {
    const colors = {
      PURCHASE: 'green',
      SALE: 'blue',
      RETURN_TO_SUPPLIER: 'orange',
      RETURN_FROM_CUSTOMER: 'cyan',
      ADJUSTMENT: 'purple',
      TRANSFER: 'magenta',
      WRITE_OFF: 'red',
      COLLECTION_ADDITION: 'lime',
      COLLECTION_REMOVAL: 'volcano',
      LOCATION_TRANSFER: 'geekblue',
      STORE_RECEIPT: 'gold',
      STORE_SHIPMENT: 'default'
    };
    return colors[type] || 'default';
  };

  const columns = [
    {
      title: 'Transaction Date',
      dataIndex: 'transactionDate',
      key: 'transactionDate',
      render: (date) => formatApiDate(date),
      sorter: (a, b) => compareApiDates(a.transactionDate, b.transactionDate),
    },
    {
      title: 'Item',
      dataIndex: 'itemName',
      key: 'itemName',
      ellipsis: true,
    },
    {
      title: 'Type',
      dataIndex: 'transactionType',
      key: 'transactionType',
      render: (type) => (
        <Tag color={getTransactionTypeColor(type)}>
          {type.replace(/_/g, ' ')}
        </Tag>
      ),
      filters: [
        { text: 'Purchase', value: 'PURCHASE' },
        { text: 'Sale', value: 'SALE' },
        { text: 'Return to Supplier', value: 'RETURN_TO_SUPPLIER' },
        { text: 'Return from Customer', value: 'RETURN_FROM_CUSTOMER' },
        { text: 'Adjustment', value: 'ADJUSTMENT' },
        { text: 'Transfer', value: 'TRANSFER' },
        { text: 'Write Off', value: 'WRITE_OFF' },
      ],
      onFilter: (value, record) => record.transactionType === value,
    },
    {
      title: 'Quantity',
      dataIndex: 'quantity',
      key: 'quantity',
      align: 'right',
      sorter: (a, b) => a.quantity - b.quantity,
    },
    {
      title: 'Unit Price',
      dataIndex: 'unitPrice',
      key: 'unitPrice',
      align: 'right',
      render: (price) => price ? `${NAIRA}${formatCurrency(price)}` : '-',
    },
    {
      title: 'Total Price',
      dataIndex: 'totalPrice',
      key: 'totalPrice',
      align: 'right',
      render: (price) => price ? `${NAIRA}${formatCurrency(price)}` : '-',
      sorter: (a, b) => (a.totalPrice || 0) - (b.totalPrice || 0),
    },
    {
      title: 'Reference',
      dataIndex: 'referenceNumber',
      key: 'referenceNumber',
      ellipsis: true,
      render: (ref) => ref || '-',
    },
    {
      title: 'Source',
      key: 'source',
      render: (_, record) => {
        if (record.sourceStoreName || record.sourceStoreDepartmentName) {
          return (
            <Tooltip title={`${record.sourceStoreName || ''} ${record.sourceStoreDepartmentName ? `- ${record.sourceStoreDepartmentName}` : ''}`}>
              <span className="truncate">
                {record.sourceStoreName || '-'}
                {record.sourceStoreDepartmentName && ` / ${record.sourceStoreDepartmentName}`}
              </span>
            </Tooltip>
          );
        }
        return '-';
      },
      ellipsis: true,
    },
    {
      title: 'Destination',
      key: 'destination',
      render: (_, record) => {
        if (record.destinationStoreName || record.destinationStoreDepartmentName) {
          return (
            <Tooltip title={`${record.destinationStoreName || ''} ${record.destinationStoreDepartmentName ? `- ${record.destinationStoreDepartmentName}` : ''}`}>
              <span className="truncate">
                {record.destinationStoreName || '-'}
                {record.destinationStoreDepartmentName && ` / ${record.destinationStoreDepartmentName}`}
              </span>
            </Tooltip>
          );
        }
        return '-';
      },
      ellipsis: true,
    },
    {
      title: 'Actions',
      key: 'actions',
      fixed: 'right',
      render: (_, record) => (
        <Space size="middle">
          <Button
            type="link"
            icon={<EyeOutlined />}
            onClick={() => onView(record)}
            size="small"
          >
            View
          </Button>
        </Space>
      ),
    },
  ];

  return (
    <Table
      columns={columns}
      dataSource={transactions}
      rowKey="id"
      loading={loading}
      pagination={{
        defaultPageSize: 10,
        showSizeChanger: true,
        showTotal: (total) => `Total ${total} transactions`,
      }}
      scroll={{ x: 1200 }}
    />
  );
};

export default TransactionTable;
